import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Radio, CalendarDays, NotebookPen, CalendarCheck, Smile, BookA } from "lucide-react";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { SchoolNext } from "@/components/school/SchoolNext";
import { GradeTrends } from "@/components/dashboard/GradeTrends";
import { http } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import { useCelebrate } from "@/components/Celebration";
import { BuddyAvatar } from "@/components/buddy/BuddyAvatar";
import { CourseWork } from "@/components/dashboard/CourseWork";
import { Agenda } from "@/components/dashboard/Agenda";
import { CatchUpCard } from "@/components/dashboard/CatchUpCard";
import { SchoolConnect } from "@/components/school/SchoolConnect";
import { PracticePanel } from "@/components/school/PracticePanel";
import { AddSchoolItem } from "@/components/school/AddSchoolItem";
import { SchoolClasses } from "@/components/school/SchoolClasses";
import { FindAssignment } from "@/components/school/FindAssignment";


const Banner = ({ live }) => {
  const { profile, buddy } = useProfile();
  const adult = profile.learner_type === "adult";
  return (
    <section className="pt-10 lg:pt-14 flex flex-wrap items-center gap-6 rise" data-testid="student-banner">
      {buddy && <div className="w-24 h-24 shrink-0 float-up"><BuddyAvatar buddy={buddy} className="w-full h-full !rounded-[1.75rem] shadow-lg" /></div>}
      <div className="flex-1 min-w-[240px]">
        <p className="text-sm text-stone-500 inline-flex items-center gap-1.5" data-testid="student-banner-date"><CalendarDays className="w-4 h-4" />{new Date().toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" })}</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#2C4A3E] mt-1" data-testid="student-banner-greeting">Good Day, {profile.name.split(" ")[0]}</h1>
        {buddy && <p className="mt-2 text-stone-600">{buddy.name} is ready to learn with you today.</p>}
      </div>
      {adult ? <Link to="/pathway" data-testid="hero-pathway-btn" className="px-7 py-4 rounded-2xl bg-[#2C4A3E] text-white font-semibold shadow-md hover:bg-[#1E352B] transition-colors">Go to my Career Pathway</Link> : (
        <Link to="/live" data-testid="banner-live-btn" className="inline-flex items-center gap-2 px-7 py-4 rounded-2xl bg-[#F28C38] text-[#3B1D05] font-semibold text-lg shadow-[0_6px_0_#C96A1B] hover:translate-y-[2px] hover:shadow-[0_4px_0_#C96A1B] transition-transform">
          <Radio className="w-5 h-5" /> Today's Live Classes{live > 0 && <span className="ml-1 px-2 py-0.5 rounded-full bg-white/80 text-sm" data-testid="banner-live-count">{live}</span>}
        </Link>
      )}
    </section>
  );
};

const QuickLinks = () => (
  <section className="mt-6 flex flex-wrap gap-3" data-testid="dash-quick-links">
    {[["/planner", "My plan", CalendarCheck], ["/journal", "Journal", NotebookPen], ["/buddy", "Buddy", Smile], ["/words", "Word bank", BookA], ["/live", "Live classes", Radio]].map(([to, label, Icon]) => (
      <Link key={to} to={to} data-testid={`quick-link-${to.slice(1)}`} className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-[#F7F3EB] border border-[#E6DEC3] text-[#2C4A3E] hover:-translate-y-0.5 transition-transform"><Icon className="w-4 h-4" />{label}</Link>))}
  </section>
);

export const StudentDashboard = ({ top, cards = [] }) => {
  const celebrate = useCelebrate();
  const { profile } = useProfile();
  const [params] = useSearchParams();
  const [f, setF] = useState({ subject: "", status: params.get("cw") || "due" });
  const [d, setD] = useState(null);
  const [showCounts, setShowCounts] = useState(() => localStorage.getItem("cl_show_counts") !== "0");
  const load = useCallback(() => http.get("/coursework/me", { params: { subject: f.subject || undefined, status: f.status || undefined } }).then((r) => setD(r.data)).catch(() => {}), [f]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const cw = params.get("cw");
    if (cw === null) return;
    setF((x) => ({ ...x, status: cw }));
    setTimeout(() => document.querySelector('[data-testid="dash-card-coursework"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
  }, [params]);
  const toggleCounts = (v) => { setShowCounts(v); localStorage.setItem("cl_show_counts", v ? "1" : "0"); };
  const toggle = async (item) => {
    setD((x) => ({ ...x, agenda: x.agenda.map((i) => (i.id === item.id ? { ...i, done: !i.done } : i)), items: x.items.filter((i) => i.id !== item.id) }));
    const r = await http.patch(`/planner/items/${item.id}`, { done: !item.done }).catch(() => null);
    if (r?.data?.celebrate) celebrate(r.data.celebrate);
    load();
  };
  const all = [
    { id: "classes", label: "Live classes", node: d && <SchoolClasses classes={d.school_classes} today={d.today} /> },
    ...cards.slice(0, 3),
    { id: "catchup", label: "Catch-up plan", node: <CatchUpCard onDone={load} /> },
    { id: "school", label: "School connect", node: <SchoolConnect onSynced={load} /> },
    { id: "find", label: "Find my assignment", node: d && <FindAssignment d={d} /> },
    { id: "schoolnext", label: "Buddy's school picks", node: d && <SchoolNext items={d.school_next} /> },
    { id: "practice", label: "Daily practice", node: <PracticePanel onChange={load} /> },
    { id: "coursework", label: "Course work", node: d && <CourseWork d={d} f={f} setF={setF} showCounts={showCounts} setShowCounts={toggleCounts} actions={<AddSchoolItem today={d.today} onAdded={load} />} onToggle={toggle} onChange={load} /> },
    { id: "trends", label: "Grade trends", node: <GradeTrends refresh={d ? d.items.map((i) => `${i.id}:${i.quiz_score ?? ""}/${i.quiz_total ?? ""}`).join("|") : ""} /> },
    { id: "agenda", label: "Agenda", node: d && <Agenda d={d} onToggle={toggle} /> },
    ...cards.slice(3),
    { id: "links", label: "Quick links", node: <QuickLinks />, defaultHidden: true },
  ];
  return (
    <>
      <Banner live={d?.live_today || 0} />
      {top}
      <DashboardLayout cards={all} initial={profile.dashboard_layout} />
    </>
  );
};
